/**
 * Excel export utility for production planning sessions.
 * Uses xlsx-js-style library for Excel files with cell styling support.
 */

import * as XLSX from 'xlsx-js-style';
import { getDateCode } from './dateUtils';

interface ProductionSession {
  session_number?: string;
  scheduled_date?: string;
  product_sku?: string;
  product_name?: string;
  product_chinese_name?: string;
  packing?: string;
  total_boxes?: number;
  target_output_weight?: number;
  raw_material_item_number?: string;
  raw_material_name?: string;
  raw_material_chinese_name?: string;
  raw_material_cases?: number;
  initial_material_weight?: number;
  brand?: string;
  distinct_brand_count?: string | number;
  material_location_code?: string;
  status?: string;
}

/**
 * Format date for display (M/D format)
 */
function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}

/**
 * Export production sessions to an Excel file
 * @param sessions - Array of production sessions to export
 * @param startDate - Start date of the export range (YYYY-MM-DD)
 * @param endDate - End date of the export range (YYYY-MM-DD)
 */
export function exportSessionsToExcel(
  sessions: ProductionSession[],
  startDate: string,
  endDate: string
): void {
  // Define column headers (Product Name includes Chinese name, empty column as separator)
  const headers = [
    'Date Code',
    'Date',
    'Item Number',
    'Product Name',
    'Packing',
    'Cases',
    'Est. Lbs',
    '',  // Empty separator column
    'Material',
    'Material Name',
    'Brand',
    'Est. Cases',
    'Est. Lbs',
    'Location',
  ];

  // Build rows with empty line breaks between different dates
  const rows: (string | number)[][] = [];
  let lastDate = '';

  sessions.forEach((session) => {
    const scheduledDate = session.scheduled_date?.split('T')[0] || '';

    // Add empty row when date changes (but not before the first row)
    if (lastDate && scheduledDate !== lastDate) {
      rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    }
    lastDate = scheduledDate;

    // Concatenate product name with Chinese name if available
    const productName = session.product_chinese_name
      ? `${session.product_name || ''} (${session.product_chinese_name})`
      : session.product_name || '';

    // Concatenate material name with Chinese name if available
    const materialName = session.raw_material_chinese_name
      ? `${session.raw_material_name || ''} (${session.raw_material_chinese_name})`
      : session.raw_material_name || '';

    const location = session.material_location_code || '';

    rows.push([
      scheduledDate ? getDateCode(scheduledDate) : '',
      scheduledDate ? formatDateShort(scheduledDate) : '',
      session.product_sku || '',
      productName,
      session.packing || '',
      session.total_boxes || '',
      session.target_output_weight ? Number(session.target_output_weight).toFixed(2) : '',
      '',  // Empty separator column
      session.raw_material_item_number || '',
      materialName,
      Number(session.distinct_brand_count) > 1 ? 'Multiple' : (session.brand || ''),
      session.raw_material_cases || '',
      session.initial_material_weight ? Number(session.initial_material_weight).toFixed(2) : '',
      location,
    ]);
  });

  // Create worksheet data with headers
  const worksheetData = [headers, ...rows];

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths for better readability
  worksheet['!cols'] = [
    { wch: 10 },  // Date Code
    { wch: 8 },   // Date
    { wch: 12 },  // Item Number
    { wch: 35 },  // Product Name (wider to fit Chinese name)
    { wch: 12 },  // Packing
    { wch: 8 },   // Cases
    { wch: 10 },  // Est. Lbs
    { wch: 3 },   // Empty separator column
    { wch: 15 },  // Material
    { wch: 35 },  // Material Name (wider to fit Chinese name)
    { wch: 15 },  // Brand
    { wch: 10 },  // Est. Cases
    { wch: 10 },  // Est. Lbs
    { wch: 10 },  // Location
  ];

  // Apply header styling (blue background with white bold text)
  const headerStyle = {
    fill: { fgColor: { rgb: '4472C4' } },  // Blue background
    font: { bold: true, color: { rgb: 'FFFFFF' } },
  };

  // Apply style to each header cell (row 1, columns A-N, skip H which is separator)
  const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'I1', 'J1', 'K1', 'L1', 'M1', 'N1'];
  headerCells.forEach((cell) => {
    if (worksheet[cell]) {
      worksheet[cell].s = headerStyle;
    }
  });

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Production Schedule');

  // Generate filename with date range
  const filename = `production-schedule-${startDate}-to-${endDate}.xlsx`;

  // Trigger download using bookType xlsx with cellStyles enabled
  XLSX.writeFile(workbook, filename, { bookType: 'xlsx', cellStyles: true });
}
